import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { CreditCard, PiggyBank, CheckCircle2, Info } from 'lucide-react';
import { Money } from '../../contexts/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '');

export const useDepositQuote = ({ passengers, source, departureDate, total, isNdc }) => {
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    if (!total || !source) return undefined;
    let cancelled = false;
    const qs = new URLSearchParams({ passengers: String(passengers || 1), source, total: String(total), is_ndc: isNdc ? 'true' : 'false' });
    if (departureDate) qs.set('departure_date', departureDate);
    fetch(`${API_URL}/api/flights/deposit/quote?${qs}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled) setQuote(j); })
      .catch(() => { if (!cancelled) setQuote(null); });
    return () => { cancelled = true; };
  }, [passengers, source, departureDate, total, isNdc]);
  return quote;
};

const Option = ({ active, onClick, icon: Icon, title, amount, note, testId, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    aria-pressed={active}
    className={`w-full text-left rounded-xl border-2 p-4 transition-colors ${active ? 'border-[#FF6600] bg-orange-50' : 'border-slate-200 bg-white hover:border-slate-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <div className="flex items-start gap-3">
      <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${active ? 'bg-[#FF6600] text-white' : 'bg-slate-100 text-slate-500'}`}><Icon className="h-5 w-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-bold text-slate-900">{title}</div>
          <div className="font-black text-slate-900 text-lg whitespace-nowrap">{amount}</div>
        </div>
        <div className="text-xs text-slate-600 mt-1">{note}</div>
      </div>
      {active && <CheckCircle2 className="h-5 w-5 text-[#FF6600] shrink-0" />}
    </div>
  </button>
);

export const FlightPaymentPlan = ({ quote, total, plan, onChange }) => {
  if (!quote) return null;
  const deposit = plan === 'DEPOSIT';
  return (
    <Card className="border-0 shadow-lg" data-testid="flight-payment-plan">
      <CardContent className="p-4 sm:p-6 space-y-3">
        <div>
          <h3 className="font-bold text-slate-900 text-base">How would you like to pay?</h3>
          <p className="text-xs text-slate-500">Pay the full fare now, or hold your seats with a small deposit and pay the balance later.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Option active={!deposit} onClick={() => onChange('FULL')} icon={CreditCard} title="Pay in full" amount={<Money gbp={Number(total).toFixed(2)} />} note="Tickets issued straight after payment." testId="payment-plan-full" />
          <Option
            active={deposit}
            onClick={() => quote.available && onChange('DEPOSIT')}
            disabled={!quote.available}
            icon={PiggyBank}
            title="Pay deposit & hold"
            amount={<Money gbp={Number(quote.deposit).toFixed(2)} />}
            note={quote.available ? <>Balance of <Money gbp={Number(quote.balance_due).toFixed(2)} /> due by {fmt(quote.balance_due_date)}.</> : quote.reason}
            testId="payment-plan-deposit"
          />
        </div>
        {deposit && quote.available && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs sm:text-sm text-amber-900" data-testid="deposit-terms">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <strong>Deposit terms:</strong> £{Number(quote.deposit).toFixed(0)} deposit for {quote.deposit >= 100 ? 'two or more passengers' : 'a single passenger'}. The deposit is refundable, subject to a £{Number(quote.admin_fee).toFixed(0)} administration fee.
              Your seats are held until <strong>{fmt(quote.balance_due_date)}</strong>; the fare is only guaranteed once the balance is paid and tickets are issued. A secure link to pay the balance online will be emailed to you.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
